/**
 * Implementação do repositório de Client: orquestra datasource remoto e local.
 */

import type { Client } from '@/core/domain';
import type { ClientRepository } from '../../domain/repositories/client-repository';
import type { ClientRemoteDataSource } from '../datasources/remote';
import type { ClientLocalDataSource } from '../datasources/local';

export class ClientRepositoryImpl implements ClientRepository {
  constructor(
    private readonly remote: ClientRemoteDataSource,
    private readonly local: ClientLocalDataSource
  ) {}

  async getById(userId: string, id: string): Promise<Client | null> {
    const fromCache = await this.local.getById(id);
    if (fromCache != null) return fromCache;
    const fromRemote = await this.remote.getById(userId, id);
    if (fromRemote != null) await this.local.create(fromRemote).catch(() => {});
    return fromRemote;
  }

  async getAllByUserId(userId: string): Promise<Client[]> {
    const fromCache = await this.local.getAllByUserId(userId);
    if (fromCache.length > 0) return fromCache;
    const fromRemote = await this.remote.getAllByUserId(userId);
    if (fromRemote.length > 0) {
      for (const client of fromRemote) {
        await this.local.create(client).catch(() => {});
      }
    }
    return fromRemote;
  }

  async create(userId: string, client: Client): Promise<Client> {
    const created = await this.remote.create(userId, client);
    await this.local.create(created).catch(() => {});
    return created;
  }

  async update(userId: string, id: string, client: Partial<Client>): Promise<Client> {
    const updated = await this.remote.update(userId, id, client);
    await this.local.update(id, updated).catch(() => {});
    return updated;
  }

  async delete(userId: string, id: string): Promise<void> {
    await this.remote.delete(userId, id);
    await this.local.delete(id).catch(() => {});
  }
}
